/**
 * The one way into standings from the league view: a labelled outline button
 * in the trailing slot of the league card's header (Scores UX spec v1.0
 * section 5) that opens `?view=standings` for the league on screen. A trophy,
 * the word "Standings" and a chevron in `primary`, on a `surface` pill with a
 * hairline `border`; 36 tall, with a transparent 44px hit slop centered on it
 * for its tap target (the same treatment as the All feed's See all), so the
 * league view's card header is exactly as tall as the All feed's. Never plain
 * text.
 *
 * Per SCORES-8 (Q66), a league with no usable standings data gets no button
 * at all, so returning `null` below is the whole of that rule.
 */
import { useLeagueCatalogGet } from "@sportsmet/sports-sdk/private";
import { cn, HIT_SLOP } from "@sportsmet/ui";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { ChevronRightIcon, TrophyIcon } from "../../icons/glyphs";
import { STANDINGS_LINK_LABEL } from "../scores/leagueSelectorContract";

/** The compact outline pill the Scores surfaces share: the Standings button
 *  and the chip row's Leagues control. A 36px visible pill inside a 44px
 *  target, the 4px above and below being transparent padding on the target. */
export const OUTLINE_PILL_TARGET_CLASS =
  "group inline-flex min-h-touch-target min-w-touch-target shrink-0 items-center justify-center no-underline focus-visible:outline-none";
export const OUTLINE_PILL_CLASS = cn(
  "inline-flex h-chip-height min-w-touch-target items-center justify-center gap-1 rounded-full border border-line bg-surface px-3 text-caption text-primary",
  "transition-colors duration-200 group-hover:bg-surface-sunken motion-reduce:transition-none",
  "group-focus-visible:outline-2 group-focus-visible:outline-offset-2 group-focus-visible:outline-focus-ring",
);

/** The outline pill as a header's trailing control: the target is the 36px
 *  pill itself, and its 44px tap height is a transparent hit slop that does
 *  not add to the header's height (spec 2.2 and 5). */
export const OUTLINE_PILL_HEADER_TARGET_CLASS = cn(
  "group relative inline-flex min-w-touch-target shrink-0 items-center justify-center no-underline focus-visible:outline-none",
  HIT_SLOP,
);

export const StandingsLinkButton = ({
  leagueId,
}: {
  leagueId: string;
}): ReactNode => {
  const catalog = useLeagueCatalogGet({});
  const league = (catalog.data?.data?.sports ?? [])
    .flatMap((sport) => sport.leagues)
    .find((entry) => entry.leagueId === leagueId);
  if (!league?.hasStandings) return null;

  return (
    <Link
      to="/scores"
      search={(prev) => ({ ...prev, leagueId, view: "standings" as const })}
      className={OUTLINE_PILL_HEADER_TARGET_CLASS}
      data-testid="league-standings-link"
    >
      <span className={OUTLINE_PILL_CLASS} data-testid="league-standings-pill">
        <TrophyIcon />
        {STANDINGS_LINK_LABEL}
        <ChevronRightIcon />
      </span>
    </Link>
  );
};
